import React, { useEffect, useState } from "react";
import {
  Input,
  Popconfirm,
  Flex,
  Tooltip,
  Tag,
  Modal,
  Form,
  Select,
  message,
} from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import useUrlFilters from "hooks/useUrlFilters";
import * as actions from "store/actions/AuthActions";
import FilterBar from 'components/Common/FilterBar';

const { TextArea } = Input;
const { Option } = Select;

// Search and paging live in the URL so a filtered list can be shared as a link.
const URL_FILTERS = {
  search: { type: "string" },
  page: { type: "number", default: 1 },
  pageSize: { type: "number", key: "page_size", default: 100 },
};

const ContractTypesTable = () => {
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const [form] = Form.useForm();

  const searchTerm = filters.search || "";
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  // Backend pagination state
  const currentPage = filters.page;
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const [totalRows, setTotalRows] = useState(0);

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingContractType, setEditingContractType] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchContractTypes = async (page, size) => {
    setLoading(true);
    try {
      const response = await actions.loadContractTypes(page, searchTerm, size);
      if (response.status === 200) {
        const data = response.data;
        setDataSource(data.results || []);
        setTotalRows(data.count || 0);
      }
    } catch (e) {
      console.error("Error fetching contract types:", e);
    }
    setLoading(false);
  };

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    } else {
      setFilters({ search: value, page: 1 });
    }
  };


  // Fetch contract types when search term or pagination changes
  useEffect(() => {
    fetchContractTypes(currentPage, pageSize);
  }, [searchTerm, currentPage, pageSize]);

  const handleDelete = async (id) => {
    const response = await actions.deleteContractType(id);
    if (response.status === 204) {
      // Refresh the data after deletion
      fetchContractTypes(currentPage, pageSize);
    }
  };

  // Modal handlers
  const handleCreateNew = () => {
    setEditingContractType(null);
    form.resetFields();
    form.setFieldsValue({ status: 'DRAFT' });
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingContractType(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingContractType) {
        await actions.updateContractType(values, editingContractType.id);
        message.success('Contract Type updated successfully');
      } else {
        await actions.addContractType(values);
        message.success('Contract Type created successfully');
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchContractTypes(currentPage, pageSize);
    } catch (error) {
      if (error?.errorFields) {
        // Form validation error, don't show message
        return;
      }
      message.error(`Failed to ${editingContractType ? 'update' : 'create'} contract type`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        "selectedRows: ",
        selectedRows
      );
    },
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "green";
      case "DRAFT":
        return "orange";
      case "ARCHIVED":
        return "gray";
      default:
        return "default";
    }
  };

  const defaultColumns = [
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a?.name || "").localeCompare(b?.name || ""),
      width: 300,
      fixed: "left",
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: "CREATED DATE",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) =>
        new Date(a?.created_at) - new Date(b?.created_at),
      width: 180,
      render: (date) => {
        if (!date) return "-";
        return new Date(date).toLocaleDateString("en-IN", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      },
    },
    {
      title: "Actions",
      dataIndex: "edit",
      key: "edit",
      width: 150,
      render: (_, record) => {
        return dataSource?.length >= 1 ? (
          <Flex gap="middle">
            <Tooltip title="Edit">
              <a>
                <EditOutlined
                  onClick={() => handleEdit(record)}
                  style={{
                    verticalAlign: "middle",
                  }}
                />
              </a>
            </Tooltip>
            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => handleDelete(record.id)}
            >
              <a>
                <DeleteOutlined />
              </a>
            </Popconfirm>
          </Flex>
        ) : null;
      },
    },
  ];

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      align: "center",
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
      }),
    };
  });

  const [selectionType] = useState("checkbox");

  // Handle pagination change
  const handleTableChange = (page, size) => {
    // Back to the first page when the page size changes.
    setFilters({ page: size !== pageSize ? 1 : page, pageSize: size });
  };

  return (
    <div style={{ padding: 10 }}>
      <div style={{ marginBottom: 8 }}>
        Showing {totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1}-
        {Math.min(currentPage * pageSize, totalRows)} of {totalRows}{" "}
        contract types
        </div>
      <Flex
        gap="large"
        justify="space-between"
        align="flex-start"
        wrap="wrap"
        style={{ width: "100%" }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <FilterBar
            searchValue={searchTermLive}
            onSearchChange={setSearchTermLive}
            onSearch={onSearch}
            searchPlaceholder="Search by name"
            onRefresh={() => fetchContractTypes(currentPage, pageSize)}
            refreshing={loading}
            addLabel="Add Contract Type"
            addIcon={<PlusOutlined />}
            onAdd={handleCreateNew}
/>
        </div>
      </Flex>
      <div style={{ marginTop: 20 }}>
        <Table
          rowSelection={{
            type: selectionType,
            ...rowSelection,
          }}
          loading={loading}
          bordered
          size="small"
          dataSource={dataSource}
          columns={columns}
          rowKey={(row) => row.id}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRows,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ["50", "100", "200"],
            onChange: handleTableChange,
          }}
          scroll={{
            x: "max-content",
            y: "calc(100vh - 350px)",
          }}
          sticky
        />
      </div>

      {/* Add/Edit Modal */}
      <Modal
        title={editingContractType ? 'Edit Contract Type' : 'Create Contract Type'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={submitting}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter contract type name' }]}
          >
            <Input placeholder="Enter contract type name" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select placeholder="Select status">
              <Option value="DRAFT">Draft</Option>
              <Option value="ACTIVE">Active</Option>
              <Option value="ARCHIVED">Archived</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="html_code"
            label="HTML Template Code"
            help="Optional: Enter HTML template code for this contract type"
          >
            <TextArea rows={10} placeholder="Enter HTML template code (optional)" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ContractTypesTable;
